import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { LayoutDashboard, Package, ShoppingCart, HeartPulse, Stamp, Sparkles, MessageSquare, LogOut, Plus, Edit, Trash2, Eye, Upload, Briefcase, ClipboardList, Shirt, Send, Download, FileText, FileArchive } from "lucide-react";
import { toast } from "sonner";

const HIDE_FIELDS = ["id"];
function labelize(k) {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
function fmtVal(k, v) {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) {
    if (v.length && typeof v[0] === "object") return v.map((i) => `${i.quantity}× ${i.name} — $${Number(i.price).toFixed(2)}${i.size ? ` (${i.size})` : ""}`).join("\n");
    return v.join(", ");
  }
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if ((k.includes("date") || k === "created_at" || k === "updated_at") && typeof v === "string") {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toLocaleString();
  }
  if ((k === "amount" || k === "price") && typeof v === "number") return `$${v.toFixed(2)}`;
  return String(v);
}

function DetailDialog({ open, onOpenChange, title, record }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-detail-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-navy-dark">{title}</DialogTitle><DialogDescription className="sr-only">Full record details</DialogDescription></DialogHeader>
        {record && (
          <dl className="divide-y divide-silver-light text-sm">
            {Object.entries(record)
              .filter(([k]) => !HIDE_FIELDS.includes(k))
              .map(([k, v]) => (
                <div key={k} className="py-2.5 grid grid-cols-3 gap-3" data-testid={`detail-${k}`}>
                  <dt className="text-navy/50 text-xs uppercase tracking-wide font-semibold">{labelize(k)}</dt>
                  <dd className="col-span-2 text-navy-dark whitespace-pre-wrap break-words">{fmtVal(k, v)}</dd>
                </div>
              ))}
          </dl>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();

  if (loading) return <div className="p-24 text-center text-navy/50">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-[80vh] max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-[240px_1fr] gap-8" data-testid="admin-dashboard">
      <aside className="lg:sticky lg:top-28 h-fit rounded-2xl bg-white ring-1 ring-silver-light p-4">
        <div className="text-xs tracking-[0.25em] uppercase text-navy/50 font-semibold mb-3 px-2">Admin</div>
        <nav className="flex flex-col gap-1">
          {[
            { to: "/admin/dashboard", label: "Overview", Icon: LayoutDashboard, testid: "adm-nav-overview" },
            { to: "/admin/dashboard/products", label: "Products", Icon: Package, testid: "adm-nav-products" },
            { to: "/admin/dashboard/orders", label: "Orders", Icon: ShoppingCart, testid: "adm-nav-orders" },
            { to: "/admin/dashboard/training", label: "Training", Icon: HeartPulse, testid: "adm-nav-training" },
            { to: "/admin/dashboard/notary", label: "Notary", Icon: Stamp, testid: "adm-nav-notary" },
            { to: "/admin/dashboard/custom", label: "Custom Orders", Icon: Sparkles, testid: "adm-nav-custom" },
            { to: "/admin/dashboard/custom-requests", label: "Custom Requests", Icon: ClipboardList, testid: "adm-nav-custom-requests" },
            { to: "/admin/dashboard/services", label: "Services", Icon: Briefcase, testid: "adm-nav-services" },
            { to: "/admin/dashboard/service-requests", label: "Service Requests", Icon: ClipboardList, testid: "adm-nav-service-requests" },
            { to: "/admin/dashboard/custom-studio", label: "Design Studio", Icon: Shirt, testid: "adm-nav-studio" },
            { to: "/admin/dashboard/contact", label: "Messages", Icon: MessageSquare, testid: "adm-nav-contact" },
          ].map((i) => (
            <NavLink
              key={i.to}
              to={i.to}
              end={i.to === "/admin/dashboard"}
              data-testid={i.testid}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium ${isActive ? "bg-navy-dark text-white" : "text-navy/70 hover:bg-slate-50"}`
              }
            >
              <i.Icon size={16} /> {i.label}
            </NavLink>
          ))}
          <button onClick={logout} className="mt-4 flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-cpr hover:bg-cpr/5" data-testid="adm-logout">
            <LogOut size={16} /> Sign out
          </button>
        </nav>
      </aside>

      <div className="min-w-0">
        <Routes>
          <Route index element={<Overview />} />
          <Route path="products" element={<ProductsAdmin />} />
          <Route path="orders" element={<OrdersAdmin />} />
          <Route path="training" element={<StatusListAdmin key="tr" title="Training Bookings" endpoint="/admin/training-bookings" statusEndpoint="training_bookings" columns={["full_name", "email", "participants", "preferred_date", "payment_status", "status"]} />} />
          <Route path="notary" element={<StatusListAdmin key="no" title="Notary Requests" endpoint="/admin/notary-requests" statusEndpoint="notary_requests" columns={["full_name", "phone", "service_type", "document_type", "preferred_date", "status"]} />} />
          <Route path="custom" element={<StatusListAdmin key="co" title="Custom Orders" endpoint="/admin/custom-orders" statusEndpoint="custom_orders" columns={["full_name", "email", "project_type", "quantity", "deadline", "status"]} />} />
          <Route path="custom-requests" element={<CustomRequestsAdmin />} />
          <Route path="services" element={<ServicesAdmin />} />
          <Route path="service-requests" element={<StatusListAdmin key="sr" title="Service Requests" endpoint="/admin/service-requests" statusEndpoint="service_requests" columns={["service_name", "area", "full_name", "email", "quantity", "payment_status", "status"]} />} />
          <Route path="custom-studio" element={<StudioAdmin />} />
          <Route path="contact" element={<ContactAdmin />} />
          <Route path="*" element={<Navigate to="." replace />} />
        </Routes>
      </div>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  if (!stats) return <div className="text-navy/50">Loading…</div>;
  const cards = [
    { label: "Revenue", value: `$${stats.revenue.toFixed(2)}`, color: "text-crafty" },
    { label: "Paid Orders", value: stats.paid_orders, color: "text-navy-royal" },
    { label: "Products", value: stats.products, color: "text-navy" },
    { label: "Services", value: stats.services ?? 0, color: "text-navy-royal" },
    { label: "Training", value: stats.training_bookings, color: "text-cpr" },
    { label: "Notary", value: stats.notary_requests, color: "text-navy-royal" },
    { label: "Custom Orders", value: stats.custom_orders, color: "text-crafty" },
    { label: "Custom Requests", value: stats.custom_requests ?? 0, color: "text-navy-royal" },
    { label: "Service Requests", value: stats.service_requests ?? 0, color: "text-navy" },
    { label: "Contact Msgs", value: stats.contact_messages, color: "text-navy" },
  ];
  return (
    <div data-testid="admin-overview">
      <h1 className="font-serif text-4xl text-navy-dark">Overview</h1>
      <p className="text-navy/60 mt-1 text-sm">Real-time snapshot of the Collective.</p>
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="p-5 rounded-2xl bg-white ring-1 ring-silver-light" data-testid={`stat-${c.label.toLowerCase().replace(/\s/g,"-")}`}>
            <div className="text-xs uppercase tracking-widest text-navy/50">{c.label}</div>
            <div className={`font-serif text-3xl mt-1 ${c.color}`}>{c.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const EMPTY_PRODUCT = { name: "", description: "", price: 0, image_url: "", category: "Custom Apparel", tags: [], in_stock: true, featured: false };
const PRODUCT_CATEGORIES = ["Custom Apparel", "Tumblers & Drinkware", "Personalized Gifts", "Home & Living", "Digital Products", "Seasonal Collections", "Resin Art"];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ProductsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(EMPTY_PRODUCT);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/products");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((prev) => ({ ...prev, image_url: `${BACKEND_URL}${data.url}` }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...editing, price: Number(editing.price), tags: typeof editing.tags === "string" ? editing.tags.split(",").map((s) => s.trim()).filter(Boolean) : (editing.tags || []) };
    try {
      if (editing.id) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      }
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-serif text-4xl text-navy-dark">Products</h1>
          <p className="text-navy/60 mt-1 text-sm">Manage Crafty Creations catalog.</p>
        </div>
        <Dialog open={editOpen} onOpenChange={(o) => { setEditOpen(o); if (!o) setEditing(EMPTY_PRODUCT); }}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditing(EMPTY_PRODUCT)} className="bg-navy-dark text-white rounded-full" data-testid="adm-add-product">
              <Plus size={16} className="mr-1" /> New Product
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-lg max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif text-2xl text-navy-dark">{editing.id ? "Edit" : "New"} Product</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3" data-testid="product-form">
              <div><Label>Name</Label><Input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="p-name" /></div>
              <div><Label>Description</Label><Textarea required rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="p-desc" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Price (USD)</Label><Input required type="number" step="0.01" min="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="p-price" /></div>
                <div>
                  <Label>Category</Label>
                  <select required value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="p-cat" className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                    {PRODUCT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Product Photo</Label>
                <div className="flex items-center gap-3 mt-1">
                  {editing.image_url ? (
                    <img src={editing.image_url} alt="preview" className="w-16 h-16 rounded-lg object-cover border border-silver-light" data-testid="p-img-preview" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center text-navy/30"><Package size={20} /></div>
                  )}
                  <label className="inline-flex items-center gap-2 rounded-md bg-navy-dark text-white px-4 py-2 text-xs font-semibold cursor-pointer hover:bg-navy-royal transition-colors" data-testid="p-upload-label">
                    <Upload size={14} /> {uploading ? "Uploading…" : "Upload Photo"}
                    <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={handleUpload} disabled={uploading} className="hidden" data-testid="p-upload-input" />
                  </label>
                </div>
                <Input className="mt-2" placeholder="…or paste an image URL" value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} data-testid="p-img" />
              </div>
              <div><Label>Tags (comma-sep)</Label><Input value={Array.isArray(editing.tags) ? editing.tags.join(", ") : editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} data-testid="p-tags" /></div>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.in_stock} onChange={(e) => setEditing({ ...editing, in_stock: e.target.checked })} /> In stock</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} data-testid="p-featured" /> Featured</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.best_seller} onChange={(e) => setEditing({ ...editing, best_seller: e.target.checked })} data-testid="p-bestseller" /> Best Seller</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.new_drop} onChange={(e) => setEditing({ ...editing, new_drop: e.target.checked })} data-testid="p-newdrop" /> New Drop</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.seasonal} onChange={(e) => setEditing({ ...editing, seasonal: e.target.checked })} data-testid="p-seasonal" /> Seasonal</label>
              </div>
              {editing.seasonal && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Season name</Label><Input value={editing.season || ""} onChange={(e) => setEditing({ ...editing, season: e.target.value })} data-testid="p-season" placeholder="Current Season Exclusive" /></div>
                  <div><Label>Season ends</Label><Input type="date" value={(editing.season_end || "").slice(0, 10)} onChange={(e) => setEditing({ ...editing, season_end: e.target.value ? new Date(e.target.value).toISOString() : null })} data-testid="p-season-end" /></div>
                </div>
              )}
              <div>
                <Label>Sizes (one per line: SIZE,upcharge,stock)</Label>
                <Textarea rows={3} data-testid="p-sizes"
                  value={(editing.sizes || []).map((s) => `${s.size},${s.upcharge},${s.stock}`).join("\n")}
                  onChange={(e) => setEditing({ ...editing, sizes: e.target.value.split("\n").map((l) => l.split(",")).filter((a) => a[0]?.trim()).map((a) => ({ size: a[0].trim(), upcharge: Number(a[1]) || 0, stock: Number(a[2]) || 0 })) })}
                  placeholder={"S,0,20\n2XL,2,8"} />
              </div>
              <div>
                <Label>Colors (one per line: NAME,hex,stock)</Label>
                <Textarea rows={3} data-testid="p-colors"
                  value={(editing.colors || []).map((c) => `${c.name},${c.hex},${c.stock}`).join("\n")}
                  onChange={(e) => setEditing({ ...editing, colors: e.target.value.split("\n").map((l) => l.split(",")).filter((a) => a[0]?.trim()).map((a) => ({ name: a[0].trim(), hex: (a[1] || "#0c2340").trim(), stock: Number(a[2]) || 0 })) })}
                  placeholder={"Black,#1a1a1a,40\nWhite,#f4f4f5,40"} />
              </div>
              <DialogFooter><Button type="submit" className="bg-navy-dark text-white rounded-full" data-testid="p-save">Save</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="mt-8 rounded-2xl bg-white ring-1 ring-silver-light overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-navy/60 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-4">Product</th>
              <th className="text-left p-4">Category</th>
              <th className="text-right p-4">Price</th>
              <th className="text-right p-4 w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (<tr><td colSpan={4} className="p-6 text-center text-navy/50">Loading…</td></tr>) :
              items.map((p) => (
                <tr key={p.id} className="border-t border-silver-light" data-testid={`adm-product-row-${p.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img src={p.image_url} alt={p.name} className="w-12 h-12 rounded-lg object-cover" />
                      <div>
                        <div className="font-medium text-navy-dark">{p.name}</div>
                        <div className="text-xs text-navy/50 line-clamp-1 max-w-md">{p.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 capitalize text-navy/70">{p.category}</td>
                  <td className="p-4 text-right font-semibold text-crafty">${p.price.toFixed(2)}</td>
                  <td className="p-4 text-right space-x-1">
                    <button onClick={() => { setEditing({ ...p, tags: p.tags || [] }); setEditOpen(true); }} className="p-2 rounded-lg hover:bg-slate-100" data-testid={`edit-${p.id}`}><Edit size={14} /></button>
                    <button onClick={() => remove(p.id)} className="p-2 rounded-lg hover:bg-cpr/10 text-cpr" data-testid={`del-${p.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OrdersAdmin() {
  const [items, setItems] = useState([]);
  const [viewing, setViewing] = useState(null);
  useEffect(() => { api.get("/admin/orders").then(({ data }) => setItems(data)); }, []);
  return (
    <div data-testid="admin-orders">
      <h1 className="font-serif text-4xl text-navy-dark">Orders</h1>
      <p className="text-navy/60 mt-1 text-sm">All store & class payment transactions. Click a row's eye to view full details.</p>
      <div className="mt-8 rounded-2xl bg-white ring-1 ring-silver-light overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-navy/60 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-4">Type</th>
              <th className="text-left p-4">Customer</th>
              <th className="text-left p-4">Items</th>
              <th className="text-right p-4">Amount</th>
              <th className="text-center p-4">Status</th>
              <th className="text-right p-4">Date</th>
              <th className="text-right p-4 w-16">View</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (<tr><td colSpan={7} className="p-6 text-center text-navy/50">No orders yet.</td></tr>) :
              items.map((o) => (
                <tr key={o.session_id} className="border-t border-silver-light" data-testid={`order-row-${o.session_id}`}>
                  <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${o.kind === "training" ? "bg-cpr/15 text-cpr" : "bg-navy-royal/15 text-navy-royal"}`}>{o.kind || "shop"}</span></td>
                  <td className="p-4">{o.customer_name || "—"}<div className="text-xs text-navy/50">{o.customer_email || "—"}</div></td>
                  <td className="p-4 text-navy/70 max-w-xs truncate">{(o.items || []).map((i) => `${i.quantity}× ${i.name}`).join(", ")}</td>
                  <td className="p-4 text-right font-semibold">${o.amount?.toFixed(2)}</td>
                  <td className="p-4 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${o.payment_status === "paid" ? "bg-crafty/20 text-navy-dark" : "bg-slate-100 text-navy/60"}`}>{o.payment_status}</span>
                  </td>
                  <td className="p-4 text-right text-xs text-navy/50">{new Date(o.created_at).toLocaleDateString()}</td>
                  <td className="p-4 text-right">
                    <button onClick={() => setViewing(o)} className="p-2 rounded-lg hover:bg-slate-100 text-navy-royal" data-testid={`order-view-${o.session_id}`}><Eye size={15} /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <DetailDialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)} title="Order Details" record={viewing} />
    </div>
  );
}

function StatusListAdmin({ title, endpoint, statusEndpoint, columns }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(endpoint);
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/${statusEndpoint}/${id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div data-testid={`admin-${statusEndpoint}`}>
      <h1 className="font-serif text-4xl text-navy-dark">{title}</h1>
      <p className="text-navy/60 mt-1 text-sm">Click the eye icon to view the full submitted form.</p>
      <div className="mt-8 rounded-2xl bg-white ring-1 ring-silver-light overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-navy/60 text-xs uppercase tracking-wider">
            <tr>
              {columns.map((c) => <th key={c} className="text-left p-4">{c.replace(/_/g, " ")}</th>)}
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (<tr><td colSpan={columns.length + 1} className="p-6 text-center text-navy/50">Loading…</td></tr>)
             : items.length === 0 ? (<tr><td colSpan={columns.length + 1} className="p-6 text-center text-navy/50">No records.</td></tr>)
             : items.map((it) => (
              <tr key={it.id} className="border-t border-silver-light" data-testid={`${statusEndpoint}-row-${it.id}`}>
                {columns.map((c) => (
                  <td key={c} className="p-4 text-navy/80">
                    {c === "status" ? (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${it.status === "completed" ? "bg-crafty/20 text-navy-dark" : it.status === "confirmed" ? "bg-navy-royal/20 text-navy-royal" : it.status === "cancelled" ? "bg-cpr/20 text-cpr" : "bg-slate-100 text-navy/60"}`}>{it.status}</span>
                    ) : (it[c] ?? "—")}
                  </td>
                ))}
                <td className="p-4 text-right whitespace-nowrap">
                  <button onClick={() => setViewing(it)} className="p-2 rounded-lg hover:bg-slate-100 text-navy-royal align-middle" data-testid={`${statusEndpoint}-view-${it.id}`}><Eye size={15} /></button>
                  <select value={it.status} onChange={(e) => setStatus(it.id, e.target.value)} className="ml-1 text-xs bg-white border border-silver-light rounded-md px-2 py-1 align-middle" data-testid={`status-select-${it.id}`}>
                    <option value="new">new</option>
                    <option value="confirmed">confirmed</option>
                    {statusEndpoint === "custom_orders" && <option value="quoted">quoted</option>}
                    {statusEndpoint === "custom_orders" && <option value="in_progress">in_progress</option>}
                    <option value="completed">completed</option>
                    <option value="cancelled">cancelled</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <DetailDialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)} title={`${title.replace(/s$/, "")} Details`} record={viewing} />
    </div>
  );
}

const EMPTY_SERVICE = { area: "Notary", name: "", description: "", price: 0, image_url: "", booking_type: "quote", unit_label: "service", active: true, featured: false, sort_order: 0 };
const SERVICE_AREAS = ["Training", "Crafty", "Notary", "Other"];

function ServicesAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(EMPTY_SERVICE);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/services");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((prev) => ({ ...prev, image_url: `${BACKEND_URL}${data.url}` }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...editing, price: Number(editing.price) || 0, sort_order: Number(editing.sort_order) || 0 };
    try {
      if (editing.id) { await api.put(`/admin/services/${editing.id}`, payload); toast.success("Service updated"); }
      else { await api.post("/admin/services", payload); toast.success("Service created"); }
      setEditOpen(false); load();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try { await api.delete(`/admin/services/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-services">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-serif text-4xl text-navy-dark">Services</h1>
          <p className="text-navy/60 mt-1 text-sm">Add offerings across any business area — book-and-pay or quote-only. New areas expand the site automatically.</p>
        </div>
        <Dialog open={editOpen} onOpenChange={(o) => { setEditOpen(o); if (!o) setEditing(EMPTY_SERVICE); }}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditing(EMPTY_SERVICE)} className="bg-navy-dark text-white rounded-full" data-testid="adm-add-service">
              <Plus size={16} className="mr-1" /> New Service
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-lg max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif text-2xl text-navy-dark">{editing.id ? "Edit" : "New"} Service</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3" data-testid="service-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Business Area</Label>
                  <select value={editing.area} onChange={(e) => setEditing({ ...editing, area: e.target.value })} data-testid="s-area" className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                    {SERVICE_AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Booking Type</Label>
                  <select value={editing.booking_type} onChange={(e) => setEditing({ ...editing, booking_type: e.target.value })} data-testid="s-booking-type" className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                    <option value="quote">Request a Quote</option>
                    <option value="payment">Book &amp; Pay (Stripe)</option>
                  </select>
                </div>
              </div>
              <div><Label>Name</Label><Input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="s-name" /></div>
              <div><Label>Description</Label><Textarea required rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="s-desc" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Price (USD)</Label><Input type="number" step="0.01" min="0" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="s-price" /></div>
                <div><Label>Unit label</Label><Input value={editing.unit_label} onChange={(e) => setEditing({ ...editing, unit_label: e.target.value })} data-testid="s-unit" placeholder="person / bundle" /></div>
                <div><Label>Sort order</Label><Input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} data-testid="s-sort" /></div>
              </div>
              <div>
                <Label>Photo (optional)</Label>
                <div className="flex items-center gap-3 mt-1">
                  {editing.image_url
                    ? <img src={editing.image_url} alt="preview" className="w-16 h-16 rounded-lg object-cover border border-silver-light" data-testid="s-img-preview" />
                    : <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center text-navy/30"><Briefcase size={20} /></div>}
                  <label className="inline-flex items-center gap-2 rounded-md bg-navy-dark text-white px-4 py-2 text-xs font-semibold cursor-pointer hover:bg-navy-royal transition-colors" data-testid="s-upload-label">
                    <Upload size={14} /> {uploading ? "Uploading…" : "Upload Photo"}
                    <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={handleUpload} disabled={uploading} className="hidden" data-testid="s-upload-input" />
                  </label>
                </div>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="s-active" /> Active (visible on site)</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} data-testid="s-featured" /> Featured</label>
              </div>
              <DialogFooter><Button type="submit" className="bg-navy-dark text-white rounded-full" data-testid="s-save">Save</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="mt-8 rounded-2xl bg-white ring-1 ring-silver-light overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-navy/60 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-4">Service</th>
              <th className="text-left p-4">Area</th>
              <th className="text-left p-4">Type</th>
              <th className="text-right p-4">Price</th>
              <th className="text-center p-4">Active</th>
              <th className="text-right p-4 w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (<tr><td colSpan={6} className="p-6 text-center text-navy/50">Loading…</td></tr>) :
              items.length === 0 ? (<tr><td colSpan={6} className="p-6 text-center text-navy/50">No services yet. Click "New Service".</td></tr>) :
              items.map((s) => (
                <tr key={s.id} className="border-t border-silver-light" data-testid={`adm-service-row-${s.id}`}>
                  <td className="p-4"><div className="font-medium text-navy-dark">{s.name}</div><div className="text-xs text-navy/50 line-clamp-1 max-w-md">{s.description}</div></td>
                  <td className="p-4 text-navy/70">{s.area}</td>
                  <td className="p-4"><span className={`px-2 py-1 rounded-full text-xs font-medium ${s.booking_type === "payment" ? "bg-navy-royal/15 text-navy-royal" : "bg-slate-100 text-navy/60"}`}>{s.booking_type === "payment" ? "Book & Pay" : "Quote"}</span></td>
                  <td className="p-4 text-right font-semibold text-navy-royal">{s.price > 0 ? `$${s.price.toFixed(2)}` : "—"}</td>
                  <td className="p-4 text-center">{s.active ? <span className="text-green-600 text-xs font-semibold">Live</span> : <span className="text-navy/40 text-xs">Hidden</span>}</td>
                  <td className="p-4 text-right space-x-1">
                    <button onClick={() => { setEditing({ ...s }); setEditOpen(true); }} className="p-2 rounded-lg hover:bg-slate-100" data-testid={`s-edit-${s.id}`}><Edit size={14} /></button>
                    <button onClick={() => remove(s.id)} className="p-2 rounded-lg hover:bg-cpr/10 text-cpr" data-testid={`s-del-${s.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StudioAdmin() {
  const [items, setItems] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get("/admin/custom-studio-orders"); setItems(data); };
  useEffect(() => { load(); }, []);

  const sendInvoice = async (o) => {
    const p = Number(price);
    if (!p || p <= 0) { toast.error("Enter a price first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/custom-studio-orders/${o.id}/invoice`, { price: p });
      toast.success("Invoice sent to " + o.email);
      setViewing(null); setPrice(""); load();
      if (data.invoice_url) window.open(data.invoice_url, "_blank");
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Invoice failed"); }
    finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/custom-studio-orders/${id}/status`, { status }); toast.success("Updated"); load(); }
    catch { toast.error("Update failed"); }
  };

  const badge = (s) => s === "paid" ? "bg-green-100 text-green-700" : s === "invoiced" ? "bg-navy-royal/15 text-navy-royal" : s === "cancelled" ? "bg-cpr/15 text-cpr" : "bg-slate-100 text-navy/60";

  return (
    <div data-testid="admin-studio">
      <h1 className="font-serif text-4xl text-navy-dark">Design Studio Orders</h1>
      <p className="text-navy/60 mt-1 text-sm">Review custom designs, set a price, and send a Stripe invoice. Status auto-updates when the client pays.</p>
      <div className="mt-8 rounded-2xl bg-white ring-1 ring-silver-light overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-navy/60 text-xs uppercase tracking-wider">
            <tr><th className="text-left p-4">Client</th><th className="text-left p-4">Shirt</th><th className="text-right p-4">Price</th><th className="text-center p-4">Status</th><th className="text-right p-4">Review</th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (<tr><td colSpan={5} className="p-6 text-center text-navy/50">No custom studio orders yet.</td></tr>) :
              items.map((o) => (
                <tr key={o.id} className="border-t border-silver-light" data-testid={`studio-row-${o.id}`}>
                  <td className="p-4">{o.full_name}<div className="text-xs text-navy/50">{o.email} · {o.phone}</div></td>
                  <td className="p-4 text-navy/70">{o.color} {o.size} × {o.quantity}</td>
                  <td className="p-4 text-right font-semibold text-navy-royal">{o.price ? `$${o.price.toFixed(2)}` : "—"}</td>
                  <td className="p-4 text-center"><span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${badge(o.status)}`}>{o.status}</span></td>
                  <td className="p-4 text-right"><button onClick={() => { setViewing(o); setPrice(o.price || ""); }} className="p-2 rounded-lg hover:bg-slate-100 text-navy-royal" data-testid={`studio-view-${o.id}`}><Eye size={15} /></button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="bg-white max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="studio-detail">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-navy-dark">Custom Design Order</DialogTitle><DialogDescription className="sr-only">Custom order design and details</DialogDescription></DialogHeader>
          {viewing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {["front_snapshot", "back_snapshot"].map((k) => (
                  <div key={k} className="text-center">
                    <div className="text-xs uppercase tracking-wide text-navy/50 font-semibold mb-1">{k.replace("_snapshot", "")}</div>
                    {viewing[k] ? <img src={viewing[k]} alt={k} className="w-full rounded-lg border border-silver-light bg-slate-50" data-testid={`studio-snap-${k}`} /> : <div className="text-xs text-navy/40 py-10 border rounded-lg">No design</div>}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-navy/50">Client:</span> {viewing.full_name}</div>
                <div><span className="text-navy/50">Email:</span> {viewing.email}</div>
                <div><span className="text-navy/50">Phone:</span> {viewing.phone}</div>
                <div><span className="text-navy/50">Qty:</span> {viewing.quantity} · {viewing.color} {viewing.size}</div>
                <div className="col-span-2"><span className="text-navy/50">Ship to:</span> {viewing.shipping_address}</div>
                <div className="col-span-2"><span className="text-navy/50">Notes:</span> {viewing.notes || "—"}</div>
                <div className="col-span-2"><span className="text-navy/50">Rights confirmed:</span> {viewing.rights_confirmed ? "Yes" : "No"}</div>
                {viewing.invoice_url && <div className="col-span-2"><a href={viewing.invoice_url} target="_blank" rel="noreferrer" className="text-navy-royal underline">View Stripe invoice »</a></div>}
              </div>
              <div className="flex flex-wrap items-end gap-3 border-t border-silver-light pt-4">
                <div><Label>Price (USD)</Label><Input type="number" step="0.01" min="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="w-32" data-testid="studio-price" /></div>
                <Button onClick={() => sendInvoice(viewing)} disabled={busy} className="bg-navy-dark hover:bg-navy-royal text-white rounded-md uppercase tracking-wide text-xs font-bold" data-testid="studio-send-invoice"><Send size={14} className="mr-1" /> {busy ? "Sending…" : (viewing.invoice_id ? "Resend Invoice" : "Approve & Send Invoice")}</Button>
                <select value={viewing.status} onChange={(e) => setStatus(viewing.id, e.target.value)} className="text-xs bg-white border border-silver-light rounded-md px-2 py-2" data-testid="studio-status">
                  {["new", "approved", "invoiced", "paid", "in_production", "completed", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CustomRequestsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/custom-requests");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const sendInvoice = async (r) => {
    const p = Number(price);
    if (!p || p <= 0) { toast.error("Enter a price first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/custom-requests/${r.id}/invoice`, { price: p });
      toast.success("Invoice sent to " + r.email);
      setViewing(null); setPrice(""); load();
      if (data.invoice_url) window.open(data.invoice_url, "_blank");
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Invoice failed"); }
    finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/custom-requests/${id}/status`, { status }); toast.success("Updated"); load(); }
    catch { toast.error("Update failed"); }
  };

  const badge = (s) => s === "paid" ? "bg-green-100 text-green-700" : s === "invoiced" ? "bg-navy-royal/15 text-navy-royal" : s === "cancelled" ? "bg-cpr/15 text-cpr" : "bg-slate-100 text-navy/60";
  const fmtDate = (v) => v ? (isNaN(new Date(v).getTime()) ? v : new Date(v).toLocaleDateString()) : "—";

  const DETAIL_ROWS = [
    ["Returning customer", (r) => r.returning_customer ? "Yes" : "No"],
    ["Quote / Invoice #", (r) => r.quote_invoice_number || "—"],
    ["Product category", (r) => r.product_category],
    ["Specific product", (r) => r.specific_product || "—"],
    ["Quantity", (r) => r.quantity],
    ["Multiple sizes / colors", (r) => r.multiple_sizes_colors ? `Yes — ${r.sizes_colors_detail || ""}` : "No"],
    ["Design description", (r) => r.design_description],
    ["Design placement", (r) => (r.design_placements || []).join(", ") || "—"],
    ["File types", (r) => (r.file_types || []).join(", ") || "—"],
    ["Design service", (r) => r.design_service],
    ["Event type", (r) => (r.event_types || []).join(", ") || "—"],
    ["Event date", (r) => fmtDate(r.event_date)],
    ["Need it by", (r) => fmtDate(r.need_by_date)],
    ["Special instructions", (r) => r.special_instructions || "—"],
    ["Agreements", (r) => [r.agree_image_rights, r.agree_no_guarantee, r.agree_design_fee, r.agree_payment_before].every(Boolean) ? "All 4 accepted ✓" : "Incomplete"],
  ];

  return (
    <div data-testid="admin-custom-requests">
      <h1 className="font-serif text-4xl text-navy-dark">Custom Order Requests</h1>
      <p className="text-navy/60 mt-1 text-sm">Review submissions & files, set a price, and send a Stripe invoice. Status auto-updates when the client pays.</p>
      <div className="mt-8 rounded-2xl bg-white ring-1 ring-silver-light overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-navy/60 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-4">Client</th>
              <th className="text-left p-4">Product</th>
              <th className="text-left p-4">Need By</th>
              <th className="text-center p-4">Files</th>
              <th className="text-right p-4">Price</th>
              <th className="text-center p-4">Status</th>
              <th className="text-right p-4">Review</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (<tr><td colSpan={7} className="p-6 text-center text-navy/50">Loading…</td></tr>) :
              items.length === 0 ? (<tr><td colSpan={7} className="p-6 text-center text-navy/50">No custom order requests yet.</td></tr>) :
              items.map((r) => (
                <tr key={r.id} className="border-t border-silver-light" data-testid={`custom-request-row-${r.id}`}>
                  <td className="p-4">{r.full_name}<div className="text-xs text-navy/50">{r.email} · {r.phone}</div></td>
                  <td className="p-4 text-navy/70">{r.product_category}<div className="text-xs text-navy/40">× {r.quantity}</div></td>
                  <td className="p-4 text-navy/70">{fmtDate(r.need_by_date)}</td>
                  <td className="p-4 text-center text-navy/70">{(r.files || []).length}</td>
                  <td className="p-4 text-right font-semibold text-navy-royal">{r.price ? `$${r.price.toFixed(2)}` : "—"}</td>
                  <td className="p-4 text-center"><span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${badge(r.status)}`}>{r.status}</span></td>
                  <td className="p-4 text-right"><button onClick={() => { setViewing(r); setPrice(r.price || ""); }} className="p-2 rounded-lg hover:bg-slate-100 text-navy-royal" data-testid={`custom-request-view-${r.id}`}><Eye size={15} /></button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="custom-request-detail">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-navy-dark">Custom Order Request</DialogTitle><DialogDescription className="sr-only">Full custom order request details and files</DialogDescription></DialogHeader>
          {viewing && (
            <div className="space-y-5">
              <div className="rounded-xl bg-slate-50 border border-silver-light p-4">
                <div className="font-serif text-lg text-navy-dark">{viewing.full_name}</div>
                <div className="text-xs text-navy/60 mt-0.5">{viewing.email} · {viewing.phone}</div>
              </div>

              <dl className="divide-y divide-silver-light text-sm">
                {DETAIL_ROWS.map(([label, fn]) => (
                  <div key={label} className="py-2.5 grid grid-cols-3 gap-3">
                    <dt className="text-navy/50 text-xs uppercase tracking-wide font-semibold">{label}</dt>
                    <dd className="col-span-2 text-navy-dark whitespace-pre-wrap break-words">{fn(viewing)}</dd>
                  </div>
                ))}
              </dl>

              {/* Files */}
              <div className="border-t border-silver-light pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-wide text-navy/50 font-semibold">Uploaded Files ({(viewing.files || []).length})</div>
                  {(viewing.files || []).length > 0 && (
                    <a
                      href={`${BACKEND_URL}/api/admin/custom-requests/${viewing.id}/download-all`}
                      className="inline-flex items-center gap-1.5 rounded-md bg-navy-dark text-white px-3 py-1.5 text-xs font-semibold hover:bg-navy-royal transition-colors"
                      data-testid="cr-download-all"
                    >
                      <FileArchive size={14} /> Download All Files
                    </a>
                  )}
                </div>
                {(viewing.files || []).length === 0 ? (
                  <div className="text-sm text-navy/40">No files were uploaded with this request.</div>
                ) : (
                  <ul className="space-y-2">
                    {viewing.files.map((f, i) => (
                      <li key={i} className="flex items-center justify-between gap-3 rounded-lg bg-white border border-silver-light px-3 py-2 text-sm" data-testid={`cr-admin-file-${i}`}>
                        <a href={`${BACKEND_URL}${f.url}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 min-w-0 hover:text-navy-royal">
                          <FileText size={15} className="text-navy-royal shrink-0" />
                          <span className="truncate text-navy-dark">{f.filename}</span>
                        </a>
                        <a href={`${BACKEND_URL}${f.url}?download=1`} download className="inline-flex items-center gap-1 rounded-md border border-navy-dark text-navy-dark px-2.5 py-1 text-xs font-semibold hover:bg-navy-dark hover:text-white transition-colors shrink-0" data-testid={`cr-file-download-${i}`}>
                          <Download size={13} /> Download
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {viewing.invoice_url && <div className="text-sm"><a href={viewing.invoice_url} target="_blank" rel="noreferrer" className="text-navy-royal underline">View Stripe invoice »</a></div>}

              <div className="flex flex-wrap items-end gap-3 border-t border-silver-light pt-4">
                <div><Label>Price (USD)</Label><Input type="number" step="0.01" min="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="w-32" data-testid="cr-invoice-price" /></div>
                <Button onClick={() => sendInvoice(viewing)} disabled={busy} className="bg-navy-dark hover:bg-navy-royal text-white rounded-md uppercase tracking-wide text-xs font-bold" data-testid="cr-send-invoice"><Send size={14} className="mr-1" /> {busy ? "Sending…" : (viewing.invoice_id ? "Resend Invoice" : "Send Stripe Invoice")}</Button>
                <select value={viewing.status} onChange={(e) => setStatus(viewing.id, e.target.value)} className="text-xs bg-white border border-silver-light rounded-md px-2 py-2" data-testid="cr-status">
                  {["new", "reviewing", "quoted", "invoiced", "paid", "in_production", "completed", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ContactAdmin() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/contact-messages").then(({ data }) => setItems(data)); }, []);
  return (
    <div data-testid="admin-contact">
      <h1 className="font-serif text-4xl text-navy-dark">Contact Messages</h1>
      <div className="mt-8 space-y-3">
        {items.length === 0 ? <div className="text-navy/50">No messages yet.</div> :
          items.map((m) => (
            <div key={m.id} className="p-5 rounded-2xl bg-white ring-1 ring-silver-light" data-testid={`msg-${m.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div>
                  <div className="font-serif text-lg text-navy-dark">{m.subject}</div>
                  <div className="text-xs text-navy/50 mt-0.5">{m.name} · {m.email} · {m.interest}</div>
                </div>
                <div className="text-xs text-navy/50">{new Date(m.created_at).toLocaleString()}</div>
              </div>
              <p className="text-sm text-navy/80 mt-3 whitespace-pre-wrap">{m.message}</p>
            </div>
          ))}
      </div>
    </div>
  );
}
